import AddIcon from '@atlaskit/icon/core/add';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { Position } from '@databases/context-menu/type';
import { messages } from '@databases/intl/messages';
import { type HoverProps } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { getService } from '@databases/service-container';
import { DatabaseChangeType } from '@databases/types/redux';
import { createEntry } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';

import { StyledDot, STYLED_DOT_RADIUS } from './AddFieldDot';

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const InsertEntryDot = styled(StyledDot)`
  left: calc(50% - ${STYLED_DOT_RADIUS / 2}px);
  ${({ position }) =>
    position === Position.AFTER
      ? css`
          bottom: -${STYLED_DOT_RADIUS / 2 + 1}px;
        `
      : css`
          top: -${STYLED_DOT_RADIUS / 2}px;
        `}
`;

export interface AddEntryDotProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  canEditContent: boolean;
  position: Position;
  hoverProps?: HoverProps;
  setEditingIds: React.Dispatch<
    React.SetStateAction<Set<{ entryId: string; fieldId: string | undefined }>>
  >;
}

/**
 * Renders a dot that for adding a new entry before or after the current entry.
 */
const AddEntryDot: FunctionComponent<AddEntryDotProps> = ({
  contentId,
  entryId,
  fieldId,
  canEditContent,
  position,
  hoverProps,
  setEditingIds,
}) => {
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const client = useAnalytics();
  const {
    view: { settings: viewSettings },
  } = useView();

  const { formatMessage } = useIntl();

  const handleInsertRow = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      const database = selectDatabaseByContentIdRequired(store.getState(), {
        contentId,
      });
      const entry = createEntry(database, viewSettings);
      const currentEntryIndex = database.entries.indexOf(entryId);
      const previousOrNextEntryIndex =
        position === Position.AFTER ? currentEntryIndex + 1 : currentEntryIndex;
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.ADD_ENTRY,
          entry,
          index: previousOrNextEntryIndex,
        })
      );
      selectionsActions.deselectAll();
      setEditingIds((prev) => {
        const newSet = new Set(prev);
        newSet.add({ entryId: entry.id, fieldId });
        return newSet;
      });
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [
      client,
      contentId,
      dispatch,
      selectionsActions,
      entryId,
      fieldId,
      position,
      setEditingIds,
      store,
      viewSettings,
    ]
  );

  if (!canEditContent) {
    return null;
  }
  return (
    <Tooltip
      content={() => formatMessage(messages['table-layout.insert-row-tooltip'])}
      position={position === Position.BEFORE ? 'top' : 'bottom'}
    >
      <InsertEntryDot
        onClick={handleInsertRow}
        position={position}
        {...hoverProps}
        data-testid={`add-entry-dot-${position === Position.BEFORE ? 'before' : 'after'}`}
      >
        <AddIcon label="" LEGACY_size="small" />
      </InsertEntryDot>
    </Tooltip>
  );
};

export default AddEntryDot;
